/**
 * RightRailInspector — Iter 198d.
 *
 * Persistent right sidebar showing the currently-selected block's key
 * properties. Squarespace's Inspector shows Layout / Design / Content
 * side-by-side; we surface the most-used quick controls here and defer to
 * the existing side panels for deep edits.
 *
 * Sections:
 *   • Header — block type + id + jump-to-section shortcut
 *   • Section height (short / normal / tall / full)
 *   • Container width (thin / narrow / wide / full)
 *   • Text alignment
 *   • Background color quick swatch
 *   • Full editor button (opens UniversalContentEditor)
 *
 * If nothing is selected, renders a helpful empty state pointing users to
 * click a block in the canvas.
 */
import React from 'react';
import {
  Sliders, PanelRightClose, PanelRightOpen, Move, Type as TypeIcon,
  Palette, Pencil, Sparkles, AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react';

const HEIGHT_PRESETS = [
  { v: 'short',  label: 'Short'  },
  { v: 'normal', label: 'Normal' },
  { v: 'tall',   label: 'Tall'   },
  { v: 'full',   label: 'Full'   },
];

const WIDTH_PRESETS = [
  { v: 'narrow', label: 'Narrow', hint: '640px'  },
  { v: 'thin',   label: 'Thin',   hint: '800px'  },
  { v: 'medium', label: 'Medium', hint: '1000px' },
  { v: 'wide',   label: 'Wide',   hint: '1200px' },
  { v: 'full',   label: 'Full',   hint: '100%'   },
];

const COLOR_SWATCHES = [
  'transparent', '#FFFFFF', '#F8FAFC', '#E4E4E7',
  '#0E0E23', '#141019', '#1E1B2E', '#2A2340',
  '#8B5CF6', '#6D28D9', '#3B82F6', '#EC4899',
];

export default function RightRailInspector({
  selectedBlock,
  onEditSection,
  onSetSectionHeight,
  onSetContainerWidth,
  onSetAlign,
  onSetBackground,
  onOpenFullEditor,
  collapsed,
  onToggleCollapse,
  onOpenAi,
}) {
  if (collapsed) {
    return (
      <div className="w-9 shrink-0 border-l border-white/[0.06] bg-[#0B0812]/95 flex flex-col items-center py-3">
        <button
          onClick={onToggleCollapse}
          className="w-7 h-7 rounded-md text-white/50 hover:text-white hover:bg-white/[0.06] flex items-center justify-center"
          title="Expand inspector"
          data-testid="right-rail-toggle"
        >
          <PanelRightOpen size={14} />
        </button>
      </div>
    );
  }

  const b = selectedBlock;
  const c = b?.content || {};
  const currentHeight = c.section_height || 'normal';
  const currentWidth = c._premium_defaults?.container_width || c.container_width || 'wide';
  const currentAlign = c._field_styles?.title?.align || c.align || 'left';
  const currentBg = c._style?.background_color || c.background_color;

  return (
    <div
      className="w-72 shrink-0 border-l border-white/[0.06] bg-[#0B0812]/95 flex flex-col overflow-hidden"
      data-testid="right-rail"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.06]">
        <div className="flex items-center gap-1.5 text-white/60 text-[10px] uppercase tracking-[0.14em] font-bold">
          <Sliders size={11} />
          Inspector
        </div>
        <button
          onClick={onToggleCollapse}
          className="w-6 h-6 rounded-md text-white/40 hover:text-white hover:bg-white/[0.06] flex items-center justify-center"
          title="Collapse inspector"
          data-testid="right-rail-toggle"
        >
          <PanelRightClose size={12} />
        </button>
      </div>

      {/* Body */}
      {!b ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.03] flex items-center justify-center mb-3">
            <Move size={20} className="text-white/25" />
          </div>
          <p className="text-white/60 text-sm font-medium">Nothing selected</p>
          <p className="text-white/35 text-xs mt-1">Click any section in the canvas to edit its layout and design here.</p>
        </div>
      ) : (
        <div className="overflow-y-auto flex-1 py-3 space-y-4" data-testid="right-rail-body">
          {/* Block header */}
          <div className="px-3">
            <div className="text-white/85 text-sm font-semibold truncate" data-testid="right-rail-block-title">
              {c.title || c.heading || c.headline || (b.type || 'section').replace(/_/g, ' ')}
            </div>
            <div className="text-white/35 text-[10px] uppercase tracking-wider mt-0.5">
              {(b.type || 'section').replace(/_/g, ' ')} · {b.id?.slice(-6) || '—'}
            </div>
          </div>

          {/* Section height */}
          <Section title="Section height">
            <ChoiceRow>
              {HEIGHT_PRESETS.map(p => (
                <ChoicePill
                  key={p.v}
                  active={currentHeight === p.v}
                  onClick={() => onSetSectionHeight?.(b.id, p.v)}
                  testId={`right-rail-height-${p.v}`}
                >
                  {p.label}
                </ChoicePill>
              ))}
            </ChoiceRow>
          </Section>

          {/* Container width */}
          <Section title="Container width">
            <ChoiceRow wrap>
              {WIDTH_PRESETS.map(p => (
                <ChoicePill
                  key={p.v}
                  active={currentWidth === p.v}
                  onClick={() => onSetContainerWidth?.(b.id, p.v)}
                  testId={`right-rail-width-${p.v}`}
                  extra={<span className="ml-1 text-white/30 text-[9px] normal-case tracking-normal">{p.hint}</span>}
                >
                  {p.label}
                </ChoicePill>
              ))}
            </ChoiceRow>
          </Section>

          {/* Alignment */}
          <Section title="Text alignment" icon={<TypeIcon size={10} />}>
            <ChoiceRow>
              <IconChoice active={currentAlign === 'left'}   onClick={() => onSetAlign?.(b.id, 'left')}   testId="right-rail-align-left"><AlignLeft size={13} /></IconChoice>
              <IconChoice active={currentAlign === 'center'} onClick={() => onSetAlign?.(b.id, 'center')} testId="right-rail-align-center"><AlignCenter size={13} /></IconChoice>
              <IconChoice active={currentAlign === 'right'}  onClick={() => onSetAlign?.(b.id, 'right')}  testId="right-rail-align-right"><AlignRight size={13} /></IconChoice>
            </ChoiceRow>
          </Section>

          {/* Background swatches */}
          <Section title="Background" icon={<Palette size={10} />}>
            <div className="grid grid-cols-6 gap-1.5" data-testid="right-rail-bg-swatches">
              {COLOR_SWATCHES.map(c => {
                const isActive = (currentBg || 'transparent') === c;
                const isTransparent = c === 'transparent';
                return (
                  <button
                    key={c}
                    onClick={() => onSetBackground?.(b.id, c)}
                    className={`w-8 h-8 rounded-md ring-2 transition-transform ${
                      isActive ? 'ring-[#8B5CF6] scale-110' : 'ring-white/10 hover:ring-white/25'
                    } ${isTransparent ? 'bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.15)_0_4px,transparent_4px_8px)]' : ''}`}
                    style={isTransparent ? {} : { backgroundColor: c }}
                    title={c}
                    data-testid={`right-rail-bg-${c.replace('#', '')}`}
                  />
                );
              })}
            </div>
          </Section>

          {/* Full editor + AI */}
          <div className="px-3 pt-2 space-y-2">
            <button
              onClick={() => onOpenFullEditor?.(b.id)}
              className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] bg-white/[0.06] hover:bg-white/[0.10] text-white border border-white/10 transition-colors"
              data-testid="right-rail-edit-full"
            >
              <Pencil size={11} /> Edit all content
            </button>
            {onOpenAi && (
              <button
                onClick={() => onOpenAi(b.id)}
                className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white hover:shadow-[0_8px_20px_rgba(139,92,246,0.4)] transition-all"
                data-testid="right-rail-ai"
              >
                <Sparkles size={11} /> Rewrite with AI
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, icon, children }) => (
  <div className="px-3">
    <div className="text-white/45 text-[10px] uppercase tracking-[0.14em] font-bold mb-1.5 flex items-center gap-1.5">
      {icon}{title}
    </div>
    {children}
  </div>
);

const ChoiceRow = ({ children, wrap }) => (
  <div className={`flex items-center gap-1 ${wrap ? 'flex-wrap' : ''}`}>{children}</div>
);

const ChoicePill = ({ active, onClick, testId, children, extra }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center px-2.5 h-7 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
      active
        ? 'bg-gradient-to-b from-[#8B5CF6]/25 to-[#6D28D9]/20 text-white ring-1 ring-[#8B5CF6]/50'
        : 'text-white/60 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] ring-1 ring-white/10'
    }`}
  >
    {children}{extra}
  </button>
);

const IconChoice = ({ active, onClick, testId, children }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`w-8 h-8 grid place-items-center rounded-md transition-all ${
      active
        ? 'bg-[#8B5CF6]/20 text-white ring-1 ring-[#8B5CF6]/50'
        : 'text-white/55 hover:text-white bg-white/[0.03] hover:bg-white/[0.08] ring-1 ring-white/10'
    }`}
  >{children}</button>
);
